import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs';

import { DEBOUNCE_TIMEOUT } from '@shared-constants/ui-constants';

import { IConfigType, ITableConfigClickValues, ITableConfigEntity } from '../../interfaces/internal-interfaces';
import { IDayGridData } from '../payroll-day-grid/payroll-day-grid.model';

@Component({
  selector: 'grid-switch',
  templateUrl: './grid-switch.component.html',
  styleUrl: './grid-switch.component.css',
  standalone: false,
})
export class GridSwitchComponent<TEntity> implements OnInit, OnChanges {
  public isClickable: boolean;
  public description = '';

  private originalValue: TEntity;
  private readonly debounceSubject$ = new Subject<TEntity>();

  @Input() public config!: ITableConfigEntity; //a config item from configs.ts Determines metadata for field
  @Input() public formLabel?: string; //Displays a mat form label when provided. If it exists it will override the value as the label on dropdowns
  @Input() public value!: TEntity;
  @Input() public display?: string;
  @Input() public shouldHighlight = false;
  @Input() public isTotalRow = false;
  @Input() public canEdit = false;

  @Output() public readonly numberBoxClicked = new EventEmitter<void>(); //when a number box is clicked
  @Output() public readonly iconClicked = new EventEmitter<ITableConfigClickValues>(); //generic when icon is clicked
  @Output() public readonly textClicked = new EventEmitter<void>(); //when clickable text is clicked
  @Output() public readonly plainButtonClicked = new EventEmitter<void>(); //when a plain button is clicked
  @Output() public readonly valueChanged = new EventEmitter<TEntity>(); //when a value changes in general

  public get upperCaseDescription() {
    if (this.description) return this.description.toUpperCase();
    else return '';
  }

  public get upperCaseStringValue() {
    if (this.value) return (this.value as string).toUpperCase();
    else return '';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].isFirstChange()) {
      this.updateDescription();
    }
  }

  public ngOnInit(): void {
    this.updateDescription();
    this.checkError();
    this.isClickable = this.config.shouldDetectClick && !this.isTotalRow;
    this.originalValue = this.value;
    const allowedTotalTypes: IConfigType[] = ['left-text', 'text', 'number-box', 'number-grid'];
    if (this.isTotalRow && !allowedTotalTypes.includes(this.config.type)) {
      //Only a few types are allow for totals, so convert everything else into text. We need to clone the object so we don't affect the config reference for the rest of the table
      this.config = structuredClone(this.config);
      this.config.type = 'text';
    }
    this.debounceSubject$.pipe(debounceTime(DEBOUNCE_TIMEOUT)).subscribe({
      next: (x) => {
        this.valueChanged.emit(x);
      },
      error: (err) => {
        console.error('Error debouncing value change:', err);
      },
    });
  }

  private updateDescription(): void {
    if (this.value && this.config?.selectOptions) {
      this.description = this.config.selectOptions.find((option) => option.value === this.value)?.description ?? '';
    } else {
      this.description = '';
    }
  }

  handleSelectValueChange() {
    this.handleValueChanged();
    this.updateDescription();
  }

  public onlyNumberKey(event: KeyboardEvent): void {
    const regex = /^[0-9]*$/; //find any digit, 0-9
    if (!regex.test(event.key) && event.key !== '.') {
      event.preventDefault();
    }
  }

  /**
   * @description returns true if value is non-number string, '0', or empty string.
   * false if value is number string.
   * @example shouldTrimDecimalPipe("0") //true
   * @example shouldTrimDecimalPipe("Equipment") //true
   * @example shouldTrimDecimalPipe("") //true
   * @example shouldTrimDecimalPipe("20.0152") //false
   * @example shouldTrimDecimalPipe(20520) //false
   */
  public shouldOmitStringsAndZeros(value: TEntity): boolean {
    return !Number(value);
  }

  public handleValueChanged(validatedNumber?: TEntity): void {
    if (validatedNumber !== undefined) {
      this.value = validatedNumber;
    }
    this.valueChanged.emit(this.value);
  }

  public handleDebouncedValueChange(): void {
    this.debounceSubject$.next(this.value);
  }

  public handleEnterPressed(event: Event): void {
    if (this.config.shouldDetectEnter) {
      event.preventDefault();
      if (this.originalValue === this.value) return null;
      this.valueChanged.emit(this.value);
    }
  }

  public handleCellClicked(): void {
    this.numberBoxClicked.emit();
  }

  public handleTextClicked(): void {
    this.textClicked.emit();
  }

  public handlePlainButtonClicked(): void {
    this.plainButtonClicked.emit();
  }

  public handleIconClick(): void {
    if (this.config.shouldDetectClick) {
      this.iconClicked.emit(this.config.clickValue);
    }
  }

  public focusInput(inputRef: HTMLInputElement): void {
    //assign this method
    inputRef.select();
  }

  public validateHours(inputRef: HTMLInputElement): void {
    let value = Number(inputRef.value);
    if (value < 0) {
      value = 0;
    } else if (value > 24) {
      value = 24;
    }
    value = Math.trunc(value * 100) / 100;
    this.handleValueChanged(value as TEntity);
  }

  public dataAsNumberGrid(data: TEntity): IDayGridData {
    //The payroll day grid component requires the data to be of this type, so this method does only that
    return data as IDayGridData;
  }

  private checkError(): void {
    if (!this.config) {
      throw new Error('You must supply a table config entity to each instance of grid switch. See configs.ts');
    }
  }
}
